import { MemberProfile } from './cognito';
import { normalizeUsPhone } from './phone';

interface MemberResponse {
  member: Record<string, unknown>;
}

export interface AdminMember {
  memberId: string;
  email: string;
  name: string;
  companyName: string;
  website: string;
  phone: string;
  emailOptIn: boolean;
  isReseller: boolean;
  proposals?: AdminProposal[];
  status: string;
  CreatedOn: string;
  UpdatedOn: string;
  InvitedOn?: string;
}

interface MembersResponse {
  members: AdminMember[];
}

export interface MemberOrderItem {
  itemId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  productType?: string;
  cartCategory?: string;
  isStoreFrontItem?: boolean;
  customizationItems: Array<{
    name: string;
    customText: string;
    color?: string;
  }>;
}

export interface MemberOrder {
  orderId: string;
  orderDate: string;
  orderTotal: number;
  status: string;
  shippingCost: number;
  taxCost: number;
  cardProcessingFee?: number;
  pickupLocal: string;
  orderCategory?: string;
  isStoreFront?: boolean;
  invoice?: OrderInvoice;
  items: MemberOrderItem[];
}

export interface OrderInvoice {
  s3Bucket: string;
  s3Prefix: string;
  CreatedOn: string;
  UpdatedOn: string;
}

export interface InvoiceDownloadResponse {
  downloadUrl: string;
  expiresInSeconds: number;
  expiresInMinutes: number;
  key: string;
  packaged: boolean;
  CreatedOn: string;
}

interface MemberOrdersResponse {
  orders: MemberOrder[];
}

export interface MemberMeeting {
  bookingUid: string;
  title: string;
  startAt: string;
  endAt: string;
  timezone: string;
  status: string;
  location?: string;
  videoCallUrl?: string;
  cancellationReason?: string;
  isDeleted: boolean;
}

interface MemberMeetingsResponse {
  meetings: MemberMeeting[];
}

export interface AdminMemberOrders {
  member: Pick<AdminMember, 'memberId' | 'email' | 'name' | 'companyName'>;
  orders: MemberOrder[];
}

interface AdminOrdersResponse {
  memberOrders: AdminMemberOrders[];
}

interface DeleteMemberResponse {
  memberId: string;
  deleted: boolean;
  softDeleted: boolean;
  member: AdminMember;
}

export interface AdminProposal {
  proposalId: string;
  proposalName: string;
  status: string;
  s3Bucket: string;
  s3Prefix: string;
  CreatedOn: string;
  UpdatedOn: string;
}

export type MemberProposal = Pick<AdminProposal,
  'proposalId' | 'proposalName' | 'status' | 'CreatedOn' | 'UpdatedOn'>;

interface MemberProposalsResponse {
  proposals: MemberProposal[];
}

interface ProposalResponse {
  proposal: AdminProposal;
  member: AdminMember;
}

export interface ProposalDownloadResponse {
  downloadUrl: string;
  expiresInSeconds: number;
  expiresInHours: number;
  key: string;
  packaged: boolean;
  emailSent: boolean;
  CreatedOn: string;
}

export interface MemberProposalDownloadResponse {
  downloadUrl: string;
  expiresInSeconds: number;
  expiresInMinutes: number;
  packaged: boolean;
  CreatedOn: string;
}

interface ProposalUploadTarget {
  fileName: string;
  contentType: string;
  key: string;
  uploadUrl: string;
}

interface ProposalUploadsResponse {
  bucket: string;
  prefix: string;
  expiresInSeconds: number;
  uploads: ProposalUploadTarget[];
}

const membersApiUrl = (process.env.REACT_APP_HOGCLOUD_MEMBERS_API_URL || '').replace(/\/$/, '');

const request = async <T = MemberResponse>(
  path: string,
  accessToken: string,
  init?: RequestInit,
): Promise<T> => {
  if (!membersApiUrl) {
    throw new Error('The Hogcloud member service is not configured.');
  }

  const response = await fetch(`${membersApiUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...init?.headers,
    },
  });
  const payload = await response.json();
  if (!response.ok) {
    const error = new Error(payload.message || 'Member request failed.');
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }
  return payload;
};

export const ensureMemberProfile = async (
  accessToken: string,
  profile: MemberProfile,
) => {
  try {
    return await request('/members/me', accessToken);
  } catch (error) {
    if ((error as Error & { status?: number }).status !== 404) {
      throw error;
    }
  }

  return request('/members/me', accessToken, {
    method: 'POST',
    body: JSON.stringify({
      name: profile.name,
      emailOptIn: profile.emailOptIn,
      ...(profile.phoneNumber ? { phone: normalizeUsPhone(profile.phoneNumber) } : {}),
    }),
  });
};

export const createMemberProfile = (
  accessToken: string,
  profile: Pick<MemberProfile, 'name' | 'companyName' | 'website' | 'phoneNumber' | 'emailOptIn' | 'isReseller'>,
) => request('/members/me', accessToken, {
  method: 'POST',
  body: JSON.stringify({
    name: profile.name,
    companyName: profile.companyName,
    website: profile.website,
    emailOptIn: profile.emailOptIn,
    isReseller: profile.isReseller,
    ...(profile.phoneNumber ? { phone: normalizeUsPhone(profile.phoneNumber) } : {}),
  }),
});

export const updateMemberProfile = (
  accessToken: string,
  profile: Pick<MemberProfile, 'name' | 'companyName' | 'website' | 'phoneNumber' | 'emailOptIn' | 'isReseller' | 'shippingAddress' | 'billingAddress' | 'billingSameAsShipping' | 'supportNotes'>,
) => request('/members/me', accessToken, {
  method: 'PATCH',
  body: JSON.stringify({
    name: profile.name,
    companyName: profile.companyName,
    website: profile.website,
    emailOptIn: profile.emailOptIn,
    isReseller: profile.isReseller,
    shippingAddress: profile.shippingAddress,
    billingAddress: profile.billingAddress,
    billingSameAsShipping: profile.billingSameAsShipping,
    supportNotes: profile.supportNotes,
    ...(profile.phoneNumber ? { phone: normalizeUsPhone(profile.phoneNumber) } : { phone: '' }),
  }),
});

export const cancelMemberService = (
  accessToken: string,
  confirmation: string,
) => request('/members/me/service-cancellation', accessToken, {
  method: 'POST',
  body: JSON.stringify({ confirmation }),
});

export const listMembers = (accessToken: string) =>
  request<MembersResponse>('/members', accessToken);

export interface CreateAdminMemberInput {
  name: string;
  email: string;
  companyName: string;
  website: string;
  phone: string;
  emailOptIn: boolean;
  isReseller: boolean;
}

export const createAdminMember = (accessToken: string, member: CreateAdminMemberInput) =>
  request<{ member: AdminMember }>('/members', accessToken, {
    method: 'POST',
    body: JSON.stringify(member),
  });

export const inviteAdminMember = (accessToken: string, memberId: string) =>
  request<{ member: AdminMember }>(`/members/${encodeURIComponent(memberId)}/invite`, accessToken, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const listMemberOrders = (accessToken: string) =>
  request<MemberOrdersResponse>('/members/me/orders', accessToken);

export const listMemberProposals = (accessToken: string) =>
  request<MemberProposalsResponse>('/members/me/proposals', accessToken);

export const createMemberProposalDownloadForIdentity = (
  accessToken: string,
  proposalId: string,
) => request<MemberProposalDownloadResponse>(
  `/members/me/proposals/${encodeURIComponent(proposalId)}/download`,
  accessToken,
  { method: 'POST', body: JSON.stringify({}) },
);

export const listMemberMeetings = (accessToken: string) =>
  request<MemberMeetingsResponse>('/members/me/meetings', accessToken);

export const cancelMemberMeeting = (
  accessToken: string,
  bookingUid: string,
  action: 'cancel' | 'reschedule',
) => request(`/members/me/meetings/${encodeURIComponent(bookingUid)}/${action}`, accessToken, {
  method: 'POST',
  body: JSON.stringify({}),
});

export const listAdminOrders = (accessToken: string) =>
  request<AdminOrdersResponse>('/members/orders', accessToken);

export const createOrderInvoice = (
  accessToken: string,
  memberId: string,
  orderId: string,
) => request<{ invoice: OrderInvoice }>(
  `/members/${encodeURIComponent(memberId)}/orders/${encodeURIComponent(orderId)}/invoice`,
  accessToken,
  { method: 'POST', body: JSON.stringify({}) },
);

export const createOrderInvoiceDownload = (accessToken: string, orderId: string) =>
  request<InvoiceDownloadResponse>(
    `/members/me/orders/${encodeURIComponent(orderId)}/invoice/download`,
    accessToken,
    { method: 'POST', body: JSON.stringify({}) },
  );

export const createAdminOrderInvoiceDownload = (
  accessToken: string,
  memberId: string,
  orderId: string,
) => request<InvoiceDownloadResponse>(
  `/members/${encodeURIComponent(memberId)}/orders/${encodeURIComponent(orderId)}/invoice/download`,
  accessToken,
  { method: 'POST', body: JSON.stringify({}) },
);

export const uploadOrderInvoiceFiles = async (
  accessToken: string,
  memberId: string,
  orderId: string,
  files: File[],
) => {
  const uploadTargets = await request<ProposalUploadsResponse>(
    `/members/${encodeURIComponent(memberId)}/orders/${encodeURIComponent(orderId)}/invoice/uploads`,
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({
        files: files.map((file) => ({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })),
      }),
    },
  );
  await Promise.all(uploadTargets.uploads.map(async (target, index) => {
    const response = await fetch(target.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': target.contentType },
      body: files[index],
    });
    if (!response.ok) throw new Error(`Unable to upload ${files[index].name}.`);
  }));
  return uploadTargets;
};

export const deleteMember = (
  accessToken: string,
  memberId: string,
  confirmation: string,
) => request<DeleteMemberResponse>(`/members/${encodeURIComponent(memberId)}`, accessToken, {
  method: 'DELETE',
  body: JSON.stringify({ confirmation }),
});

export const createMemberProposal = (
  accessToken: string,
  memberId: string,
  proposalName: string,
) => request<ProposalResponse>(`/members/${encodeURIComponent(memberId)}/proposals`, accessToken, {
  method: 'POST',
  body: JSON.stringify({ proposalName }),
});

export const createMemberProposalDownload = (
  accessToken: string,
  memberId: string,
  proposalId: string,
  email = false,
  existingDownload?: ProposalDownloadResponse,
) => request<ProposalDownloadResponse>(
  `/members/${encodeURIComponent(memberId)}/proposals/${encodeURIComponent(proposalId)}/download`,
  accessToken,
  {
    method: 'POST',
    body: JSON.stringify({
      email,
      ...(existingDownload ? {
        downloadUrl: existingDownload.downloadUrl,
        downloadCreatedOn: existingDownload.CreatedOn,
      } : {}),
    }),
  },
);

export const uploadMemberProposalFiles = async (
  accessToken: string,
  memberId: string,
  proposalId: string,
  files: File[],
) => {
  const uploadTargets = await request<ProposalUploadsResponse>(
    `/members/${encodeURIComponent(memberId)}/proposals/${encodeURIComponent(proposalId)}/uploads`,
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({
        files: files.map((file) => ({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })),
      }),
    },
  );

  await Promise.all(uploadTargets.uploads.map(async (target, index) => {
    const file = files[index];
    const response = await fetch(target.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': target.contentType,
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Unable to upload ${file.name}.`);
    }
  }));

  return uploadTargets;
};
